import { useEffect, useRef, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';

/**
 * A dropdown in the dark top bar: a group of pages ("Company") or the account menu
 * (the avatar). items: [{ to, label, end? } | { label, onClick, danger? } | { divider }
 * | { heading }]. Opens on click, closes on a click elsewhere, Escape (focus back to the
 * button), a choice, or a page change. Arrow keys move through the items.
 */
export default function HeaderMenu({ trigger, label, items, active = false, align = 'left', className = '' }) {
  const [open, setOpen] = useState(false);
  const wrap = useRef(null);
  const button = useRef(null);
  const location = useLocation();

  useEffect(() => { setOpen(false); }, [location.pathname]);
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => { if (!wrap.current?.contains(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === 'Escape') { setOpen(false); button.current?.focus(); } };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onDown); document.removeEventListener('keydown', onKey); };
  }, [open]);

  const move = (e) => {
    if (!['ArrowDown', 'ArrowUp'].includes(e.key)) return;
    e.preventDefault();
    const all = [...wrap.current.querySelectorAll('[role="menuitem"]')];
    const i = all.indexOf(document.activeElement);
    all[(i + (e.key === 'ArrowDown' ? 1 : all.length - 1)) % all.length]?.focus();
  };
  const openWithKeys = (e) => {
    if (e.key !== 'ArrowDown') return;
    e.preventDefault(); setOpen(true);
    setTimeout(() => wrap.current?.querySelector('[role="menuitem"]')?.focus(), 0);
  };

  return (
    <div className={`hmenu ${className}`} ref={wrap}>
      <button ref={button} type="button" className={`hmenu-trigger${active ? ' active' : ''}`} aria-haspopup="menu" aria-expanded={open}
        aria-label={label} onClick={() => setOpen((o) => !o)} onKeyDown={openWithKeys}>
        {trigger} <span className="hmenu-caret" aria-hidden="true">▾</span>
      </button>
      {open && (
        <div className={`hmenu-list hmenu-${align}`} role="menu" onKeyDown={move}>
          {items.filter(Boolean).map((it, n) => {
            if (it.divider) return <div key={`d${n}`} className="hmenu-divider" role="separator" />;
            if (it.heading) return <div key={`h${n}`} className="hmenu-heading">{it.heading}</div>;
            if (it.to) {
              return (
                <NavLink key={it.to} to={it.to} end={it.end} role="menuitem" className={({ isActive }) => `hmenu-item${isActive ? ' is-current' : ''}`}
                  onClick={() => setOpen(false)}>{it.label}</NavLink>
              );
            }
            return (
              <button key={`b${n}`} type="button" role="menuitem" className={`hmenu-item${it.danger ? ' is-danger' : ''}${it.current ? ' is-current' : ''}`}
                aria-current={it.current || undefined} onClick={() => { setOpen(false); it.onClick(); }}>{it.label}</button>
            );
          })}
        </div>
      )}
    </div>
  );
}

/** Initials in a circle: "Mark Vale" → "MV". */
export function Avatar({ user, size = 32 }) {
  const initials = `${user?.first_name?.[0] || ''}${user?.last_name?.[0] || ''}`.toUpperCase() || (user?.email?.[0] || '?').toUpperCase();
  return <span className="avatar" style={{ width: size, height: size, fontSize: Math.round(size * 0.4) }} aria-hidden="true">{initials}</span>;
}
